import React from 'react';
import './BookCreationPopup.css';

interface BookCreationPopupProps {
  onClose: () => void;
  currentStep?: 'submitted' | 'generation' | 'verification' | 'ready';
}

const BookCreationPopup: React.FC<BookCreationPopupProps> = ({ 
  onClose,
  currentStep = 'generation'
}) => {
  // Calculate progress percentage based on current step
  const getStepStatus = (step: string) => {
    const steps = ['submitted', 'generation', 'verification', 'ready'];
    const currentIndex = steps.indexOf(currentStep);
    const stepIndex = steps.indexOf(step);
    
    if (stepIndex < currentIndex) {
      return 'completed';
    } else if (stepIndex === currentIndex) {
      return 'active';
    } else {
      return '';
    }
  };

  // Calculate width for the colored progress line
  const getProgressLineWidth = () => {
    switch (currentStep) {
      case 'submitted':
        return '0%';
      case 'generation':
        return '33%';
      case 'verification':
        return '66%';
      case 'ready':
        return '100%';
      default:
        return '33%';
    }
  };

  // Get the appropriate icon for each step based on its status
  const getStepIcon = (step: string) => {
    const status = getStepStatus(step);
    
    if (status === 'completed') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
        </svg>
      );
    }
    
    if (status === 'active') {
      // Return animated or highlighted icon for active step
      switch (step) {
        case 'submitted':
          return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="#333"/>
            </svg>
          );
        case 'generation':
          return (
            <div className="rotating-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill="#333"/>
              </svg>
            </div>
          );
        case 'verification':
          return (
            <div className="pulse-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" fill="#333"/>
              </svg>
            </div>
          );
        case 'ready':
          return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#333"/>
            </svg>
          );
        default:
          return <span className="step-icon">•</span>;
      }
    }
    
    // Default icons for inactive steps
    switch (step) {
      case 'submitted':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
          </svg>
        );
      case 'generation':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill="currentColor"/>
          </svg>
        );
      case 'verification':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" fill="currentColor"/>
          </svg>
        );
      case 'ready':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
          </svg>
        );
      default:
        return <span className="step-icon">•</span>;
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-container">
        <button className="close-button" onClick={onClose}>
          ✕
        </button>
        <h2 className="popup-title">We are creating your Book!</h2>
        <div className="divider"></div>
        <p className="popup-message">
          We will send you the book to your <strong>email</strong> once it's ready.
        </p>
        
        <div className="progress-container">
          <div className="progress-steps">
            <div className="progress-line" style={{ width: getProgressLineWidth() }}></div>
            <div className={`progress-step ${getStepStatus('submitted')}`}>
              <div className="step-indicator">
                {getStepIcon('submitted')}
              </div>
              <span className="popup-step-label">Story submitted</span>
            </div>
            
            <div className={`progress-step ${getStepStatus('generation')}`}>
              <div className="step-indicator">
                {getStepIcon('generation')}
              </div>
              <span className="popup-step-label">Story generation</span>
            </div>
            
            <div className={`progress-step ${getStepStatus('verification')}`}>
              <div className="step-indicator">
                {getStepIcon('verification')}
              </div>
              <span className="popup-step-label">Verification</span>
            </div>
            
            <div className={`progress-step ${getStepStatus('ready')}`}>
              <div className="step-indicator">
                {getStepIcon('ready')}
              </div>
              <span className="popup-step-label">Story ready</span>
            </div>
          </div>
        </div>
        
        <button className="ok-button" onClick={onClose}>
          Ok
        </button>
      </div>
    </div>
  );
};

export default BookCreationPopup; 